import { useState, useMemo } from 'react';
import { Typography, Grid } from '@mui/material';
import { useQueryClient } from 'react-query';
import { isDate } from 'validator';

// Our components
import Alert from 'components/Alert';
import Dropdown from 'components/Dropdown/Dropdown';
import { PrimaryButton } from 'components/Button/Button';
import CurrencyTextInput from 'components/Input/CurrencyTextInput';
import Input from 'components/Input/TextInput';
import Loader from 'components/Loader';
import LoanInquirySuccessModal from 'components/Modal/LoanInquiryConfirmationModal';
import RateInput from 'components/Input/RateInput';
import SoraTextField from 'components/Input/SoraTextField';
import { TEXT, NUMBER } from 'components/Input/Types';
import RequiredFieldsTooltip from 'components/Forms/NewLoanForms/RequiredFieldsTooltip';

// Our Query Keys
import { ERROR_MESSAGE_DATA } from 'shared/query-keys';

// Our hooks 🪝
import useMutateSaveNewLoanInquiry from 'hooks/newLoanInquiries/useMutateSaveNewLoanInquiry';
import useMutateUpdateLoanInquiry from 'hooks/newLoanInquiries/useMutateUpdatedLoanInquiry';
import useMutateSendGenericEmail from 'hooks/emails/useMutateSendGenericEmail';
import usePrepopulateState from 'hooks/prepopulate/usePrepopulateState';

// Create Deal Hook
import useMutateCreateDeal from 'hooks/hubspot/useMutateCreateDeal';

// Utils
import { clearFormValues, isSubmissionReady } from 'shared/utils';
import createDefaultCloseDate from 'shared/utils/hubspot/createDefaultCloseDate';
import formatCollection from 'shared/utils/formatting/currency/formatCollection';

// Our Emails
import lendingEmailTemplate from 'emails/lendingEmailTemplate';

// Prop Types
import {
	advisorInfoProps,
	clientInfoProps,
	financialDataProps,
	autoPropTypes,
	lendingPropTypes
} from 'shared/propTypes/lendingPropTypes';

// Constants
import { US_STATE_OPTIONS, AUTO, REFINANCE_OFFER_TYPE } from 'shared/constants';
import YearDatePicker from 'components/DatePicker/YearDatePicker';

const VEHICLE_CONDITION_OPTIONS = ['Excellent', 'Good', 'Fair', 'Poor'];

function AutoRefiForm({
	advisorInfo,
	clientInfo,
	isClientNameProvided,
	loanType,
	loanRequestId,
	financialData,
	clientVehicleInfo
}) {
	const queryClient = useQueryClient();
	const currentURL = window.location.href;
	const atLoanRequestDetailsUrl = currentURL.includes('loan-request-details');

	// API Calls
	const saveLoanInquiry = useMutateSaveNewLoanInquiry();
	const { isLoading: savingLoanInquiry } = saveLoanInquiry;

	// Mutations
	const updateLoanInquiry = useMutateUpdateLoanInquiry();
	const sendGenericEmail = useMutateSendGenericEmail();
	const createDeal = useMutateCreateDeal();

	// Client Info
	const {
		clientId,
		clientFullName,
		creditScoreValue,
		emailAddressValue,
		firstName,
		lastName
	} = clientInfo;

	// Advisor Info
	const { advisorName, advisorEmail, advisorCompany, advisorWebsite } =
		advisorInfo;

	// Financial Data
	const {
		currentOutstandingBalance,
		currentLoanInterestRateValue,
		currentLoanRemainingTermValue,
		refinancingTradelineId
	} = financialData;

	const {
		vin,
		mileage,
		condition,
		state: stateValue,
		year
	} = clientVehicleInfo;

	// State
	const [vehicleState, setVehicleState] = useState('');
	const [currentInterestRate, setCurrentInterestRate] = useState('');
	const [tradelineId, setTradelineId] = useState('');
	const [currentRemainingTerm, setCurrentRemainingTerm] = useState('');
	const [loanAmount, setLoanAmount] = useState('');
	const [dob, setDOB] = useState('');
	const [vehicleVINNumber, setVehicleVINNumber] = useState('');
	const [vehicleMileage, setVehicleMileage] = useState('');
	const [vehicleCondition, setVehicleCondition] = useState('Good');
	const [yearOfVehicle, setYearOfVehicle] = useState(new Date());

	const [additionalNotes, setAdditionalNotes] = useState('');
	const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);

	const formData = [
		isClientNameProvided,
		loanAmount,
		currentRemainingTerm,
		currentInterestRate
	];

	const loanRequestDetailsRequiredFields = [
		loanAmount,
		currentInterestRate,
		currentRemainingTerm,
		vehicleState,
		vehicleVINNumber,
		mileage
	];

	const isFormReady = useMemo(
		() =>
			isSubmissionReady(
				atLoanRequestDetailsUrl
					? loanRequestDetailsRequiredFields
					: formData
			) && isClientNameProvided,
		atLoanRequestDetailsUrl ? loanRequestDetailsRequiredFields : formData
	);

	usePrepopulateState(
		[
			{
				setState: setVehicleCondition,
				value: condition
			},
			{
				setState: setVehicleMileage,
				value: mileage
			},
			{
				setState: setVehicleVINNumber,
				value: vin
			},
			{
				setState: setTradelineId,
				value: refinancingTradelineId
			},
			{
				setState: setYearOfVehicle,
				value: year
			},
			{
				setState: setLoanAmount,
				value: currentOutstandingBalance
			},
			{
				setState: setCurrentInterestRate,
				value: `${currentLoanInterestRateValue}`
			},
			{
				setState: setCurrentRemainingTerm,
				value: currentLoanRemainingTermValue
			},
			{
				setState: setVehicleState,
				value: stateValue
			}
		],
		[
			condition,
			mileage,
			vin,
			refinancingTradelineId,
			currentOutstandingBalance,
			currentLoanInterestRateValue,
			currentLoanRemainingTermValue
		]
	);

	const sendFormData = () => {
		const sendToArray = ['lending@sorafinance.com'];
		const subject = `Automotive Refinance Loan Request - ${firstName} ${lastName}`;

		const emailTemplate = lendingEmailTemplate({
			advisorInfo,
			clientInfo,
			financialData,
			clientFullName,
			loanAmount,
			loanRelatedDetails: [
				`State of vehicle purchase: ${vehicleState}`,
				`Vehicle VIN number: ${vehicleVINNumber}`,
				`Vehicle estimated mileage: ${vehicleMileage}`,
				`Vehicle condition: ${vehicleCondition}`,
				`Year of vehicle: ${yearOfVehicle}`
			],
			additionalNotes,
			lendingTypeRequested: 'Automotive Refinance Loan'
		});

		sendGenericEmail.mutate(
			{
				firstName,
				lastName,
				subject,
				message: emailTemplate,
				sendToArray
			},
			{
				onSuccess: () => {
					clearFormValues([
						setAdditionalNotes,
						setDOB,
						setCurrentInterestRate,
						setCurrentRemainingTerm,
						setLoanAmount,
						setVehicleState,
						setVehicleCondition,
						setVehicleMileage,
						setVehicleVINNumber,
						setYearOfVehicle
					]);
				}
			}
		);
	};

	const submitNewLoanInquiry = () => {
		const newLoanInquiryData = {
			additionalNotes,
			clientId,
			dob,
			emailAddressValue,
			currentRemainingTerm,
			currentInterestRate,
			firstName,
			lastName,
			loanAmount,
			offerType: REFINANCE_OFFER_TYPE,
			state: vehicleState,
			tradelineType: AUTO,
			tradelineId,
			vehicleCondition,
			vehicleMileage,
			vehicleVINNumber,
			yearOfVehicle
		};

		// Call mutation to update user's data
		saveLoanInquiry.mutate(newLoanInquiryData, {
			onSuccess: () => {
				setIsSuccessModalOpen(true);
			},
			onError: () => {
				// Set error message
				queryClient.setQueryData(
					ERROR_MESSAGE_DATA,
					'There was an error making your loan request.'
				);
			}
		});
	};

	const updateLoanRequest = () => {
		const updatedLoanInquiryData = {
			additionalNotes,
			clientId,
			dob,
			emailAddressValue,
			firstName,
			currentRemainingTerm,
			currentInterestRate,
			lastName,
			loanAmount,
			offerType: REFINANCE_OFFER_TYPE,
			state: vehicleState,
			tradelineType: AUTO,
			vehicleCondition,
			vehicleMileage,
			vehicleVINNumber,
			yearOfVehicle
		};

		// Call mutation to update user's data
		updateLoanInquiry.mutate(
			{ loanRequestId, updatedLoanInquiryData },
			{
				onSuccess: () => {
					clearFormValues([
						setAdditionalNotes,
						setDOB,
						setCurrentInterestRate,
						setCurrentRemainingTerm,
						setLoanAmount,
						setVehicleState,
						setVehicleCondition,
						setVehicleMileage,
						setVehicleVINNumber,
						setYearOfVehicle
					]);
				},
				onError: () => {
					// Set error message
					queryClient.setQueryData(
						ERROR_MESSAGE_DATA,
						'There was an error making your loan request.'
					);
				}
			}
		);
	};

	const isValidDate = useMemo(() => {
		const isStringDate = typeof yearOfVehicle === 'string';
		const isDateType = yearOfVehicle instanceof Date;
		if (isStringDate && yearOfVehicle !== '') {
			const isDateCheckWithConversion = isDate(new Date(yearOfVehicle));
			return isDateCheckWithConversion;
		}

		if (isDateType) {
			return isDate(yearOfVehicle);
		}
		// if yearOfVehicle is null this returns false;
		return false;
	}, [yearOfVehicle]);

	const submitForm = (event) => {
		event.preventDefault();

		const closeDate = createDefaultCloseDate();
		const valuesToFormat = [
			{
				type: 'percent',
				value: currentInterestRate,
				label: 'Current loan annual interest rate:'
			},
			{
				type: 'currency',
				value: loanAmount,
				label: 'Current outstanding loan balance:'
			}
		];

		const formattedCollection = formatCollection(valuesToFormat);
		const advisorNotes = `Advisor's name: ${advisorName}
				Advisor's email: ${advisorEmail}
				Advisor's company: ${advisorCompany}
				Credit Score: ${creditScoreValue}
				${formattedCollection}
				Remaining Term: ${currentRemainingTerm === '' ? 'N/A' : currentRemainingTerm}
				State: ${vehicleState}
				Vehicle VIN: ${vehicleVINNumber}
				Vehicle Mileage: ${vehicleMileage}
				Vehicle Condition: ${vehicleCondition}
				Year of Vehicle: ${yearOfVehicle === '' ? 'N/A' : yearOfVehicle}
				------------------------------------------------------------------------------------------------
				Advisor's notes: ${additionalNotes}`;

		createDeal.mutate({
			advisorInfo: {
				advisorName,
				email: advisorEmail,
				companyName: advisorCompany,
				companyDomain: advisorWebsite
			},
			properties: {
				amount: loanAmount,
				dealname: `${advisorName} / ${clientFullName}`,
				dealtype: loanType,
				borrower: clientFullName,
				closedate: closeDate
			},
			noteInfo: {
				hs_note_body: advisorNotes
			}
		});
		// If we're at /loan-inquiry, perform the following
		if (!atLoanRequestDetailsUrl) {
			submitNewLoanInquiry();
			sendFormData();
		}
		// Else if we're at /loan-inquiry-details, perform the following
		else if (atLoanRequestDetailsUrl) {
			// Call new mutations here patching the new loan inquiry
			updateLoanRequest(loanRequestId);
		}
	};

	// Handlers for mutation
	const { isError: sendFormDataError } = sendFormData;

	return (
		<>
			{!atLoanRequestDetailsUrl && (
				<Grid item xs={12}>
					<Typography variant="h2Gascogne">
						Automotive Loan Refinance
					</Typography>

					<Typography variant="body2">
						Fill in the information below to submit a loan request
						to Sora. You will be able to complete and review your
						loan request in the dashboard task bar.
					</Typography>
				</Grid>
			)}

			{savingLoanInquiry && <Loader />}
			{!savingLoanInquiry && (
				<>
					{sendFormDataError && <Alert variant="error" />}

					{/* Modal shown on successful submission of  */}
					<LoanInquirySuccessModal isOpen={isSuccessModalOpen} />

					<Grid item xs={12} md={6}>
						{/* Desired Loan Amount */}
						<CurrencyTextInput
							label="Current outstanding loan balance *"
							value={loanAmount}
							onChange={setLoanAmount}
							type={NUMBER}
							inputProps={{
								'data-test': 'loanAmount'
							}}
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						{/* Outstanding Mortgage Interest Rate */}
						<RateInput
							rate={currentInterestRate}
							setRate={setCurrentInterestRate}
							disabled={atLoanRequestDetailsUrl}
							label="Current loan annual interest rate *"
							inputProps={{
								'data-test': 'currentLoanAnnualInterestRate'
							}}
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						<Input
							type={TEXT}
							label="Remaining term *"
							value={currentRemainingTerm}
							onChange={setCurrentRemainingTerm}
							inputProps={{
								'data-test': 'currentRemainingTerm'
							}}
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						{/* State */}
						<Dropdown
							dataTestTag="state"
							variant="outlined"
							label={
								atLoanRequestDetailsUrl ? 'State *' : 'State'
							}
							items={US_STATE_OPTIONS}
							selected={vehicleState}
							onChange={setVehicleState}
							placeholder="State"
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						<Input
							type={TEXT}
							label={
								atLoanRequestDetailsUrl
									? 'Vehicle VIN or license plate number *'
									: 'Vehicle VIN or license plate number'
							}
							value={vehicleVINNumber}
							onChange={setVehicleVINNumber}
							inputProps={{
								'data-test': 'vinNumber'
							}}
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						<Input
							type={NUMBER}
							label={
								atLoanRequestDetailsUrl
									? 'Vehicle mileage *'
									: 'Vehicle mileage'
							}
							subLabel="Estimate is fine"
							value={vehicleMileage}
							onChange={setVehicleMileage}
							inputProps={{
								'data-test': 'mileage'
							}}
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						<Dropdown
							dataTestTag="condition"
							items={VEHICLE_CONDITION_OPTIONS}
							selected={vehicleCondition}
							onChange={setVehicleCondition}
							variant="outlined"
							label="Vehicle condition"
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						<YearDatePicker
							id="yearOfVehicle"
							label="Vehicle year"
							onChange={setYearOfVehicle}
							value={yearOfVehicle}
							error={yearOfVehicle === null || !isValidDate}
							errorText="Year of vehicle needs to be a valid date"
							inputProps={{
								'data-test': 'yearOfVehicle'
							}}
						/>
					</Grid>

					<Grid item xs={12}>
						<SoraTextField
							label="Additional Notes"
							value={additionalNotes}
							onChange={setAdditionalNotes}
						/>
					</Grid>
				</>
			)}

			<Grid item xs={12}>
				{!isFormReady && (
					<RequiredFieldsTooltip>
						<span>
							<PrimaryButton
								disabled={!isFormReady}
								onClick={submitForm}
							>
								Submit
							</PrimaryButton>
						</span>
					</RequiredFieldsTooltip>
				)}
				{isFormReady && (
					<PrimaryButton onClick={submitForm}>Submit</PrimaryButton>
				)}
			</Grid>
		</>
	);
}

AutoRefiForm.propTypes = {
	advisorInfo: advisorInfoProps.isRequired,
	clientInfo: clientInfoProps.isRequired,
	...lendingPropTypes,
	...autoPropTypes,
	financialData: financialDataProps.isRequired
};

export default AutoRefiForm;
